import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, User, Waypoints } from 'lucide-react';
import {
  Button,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  useToast,
  type SelectOption,
  type TabItem,
} from '../../ui';
import { useWorkstreams } from '../../features/workstreams/useWorkstreams';
import {
  collectEngineers,
  collectStages,
  filterWorkstreams,
  groupByEngineer,
  groupByStage,
} from '../../features/workstreams/workstreamModel';
import { stageLabel } from '../../features/workstreams/workstreamConfig';
import type {
  WorkstreamFilter,
  WorkstreamGrouping,
  WorkstreamSummary,
} from '../../features/workstreams/types';
import { WorkstreamBoard } from './WorkstreamBoard';
import { WorkstreamModal } from './WorkstreamModal';

const GROUP_TABS: TabItem[] = [
  { id: 'stage', label: 'By stage', icon: <Waypoints className="h-4 w-4" /> },
  { id: 'engineer', label: 'By engineer', icon: <User className="h-4 w-4" /> },
];

const EMPTY_FILTER: WorkstreamFilter = { engineer: '', stage: '' };

/**
 * Workstreams page — the "what is everyone working on?" board. Groups
 * workstreams by stage or by engineer (toggle), filterable by engineer/stage,
 * with a follow action per card and a create modal (incl. AI structuring).
 * Reads exclusively through the useWorkstreams hook.
 */
export function WorkstreamsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    workstreams,
    goals,
    currentAlias,
    loading,
    error,
    create,
    toggleFollow,
    structureFromNotes,
  } = useWorkstreams();

  const [grouping, setGrouping] = useState<WorkstreamGrouping>('stage');
  const [filter, setFilter] = useState<WorkstreamFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);

  const engineerOptions = useMemo<SelectOption[]>(
    () => collectEngineers(workstreams).map((e) => ({ value: e, label: e })),
    [workstreams],
  );
  const stageOptions = useMemo<SelectOption[]>(
    () => collectStages(workstreams).map((s) => ({ value: s, label: stageLabel(s) })),
    [workstreams],
  );

  const filtered = useMemo(
    () => filterWorkstreams(workstreams, filter),
    [workstreams, filter],
  );
  const groups = useMemo(
    () => (grouping === 'stage' ? groupByStage(filtered) : groupByEngineer(filtered)),
    [grouping, filtered],
  );
  const filtersActive = !!(filter.engineer || filter.stage);

  const openWorkstream = (w: WorkstreamSummary) =>
    navigate(`/workstreams/${encodeURIComponent(w.id)}`);

  const onToggleFollow = async (w: WorkstreamSummary) => {
    await toggleFollow(w.id);
  };

  const onCreate = async (draft: Parameters<typeof create>[0]) => {
    const saved = await create(draft);
    toast({ tone: 'success', title: 'Workstream created', description: saved.title });
    return saved;
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      <Tabs
        items={GROUP_TABS}
        value={grouping}
        onChange={(id) => setGrouping(id as WorkstreamGrouping)}
      />
      <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
        New Workstream
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        icon={<Waypoints className="h-5 w-5" />}
        title="Workstreams"
        description="What is everyone working on? Follow the work you care about."
        actions={headerActions}
      />

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select
            label="Engineer"
            placeholder="All engineers"
            value={filter.engineer}
            options={engineerOptions}
            onChange={(e) => setFilter((f) => ({ ...f, engineer: e.target.value }))}
          />
        </div>
        <div className="w-48">
          <Select
            label="Stage"
            placeholder="All stages"
            value={filter.stage}
            options={stageOptions}
            onChange={(e) => setFilter((f) => ({ ...f, stage: e.target.value }))}
          />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {workstreams.length}{' '}
          {workstreams.length === 1 ? 'workstream' : 'workstreams'}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading workstreams" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load workstreams"
          description={error}
        />
      ) : workstreams.length === 0 ? (
        <EmptyState
          icon={<Waypoints className="h-6 w-6" />}
          title="No workstreams yet"
          description="Create the first workstream so the team can see what's in flight."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              New Workstream
            </Button>
          }
        />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Waypoints className="h-6 w-6" />}
          title="No workstreams match these filters"
          description="Try clearing the filters to see all in-flight work."
        />
      ) : (
        <WorkstreamBoard
          groups={groups}
          grouping={grouping}
          currentAlias={currentAlias}
          onOpen={openWorkstream}
          onToggleFollow={onToggleFollow}
        />
      )}

      <WorkstreamModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        goals={goals}
        defaultEngineer={currentAlias}
        onCreate={onCreate}
        onStructure={structureFromNotes}
      />
    </div>
  );
}

export default WorkstreamsPage;
